import { Component, Injector, Input, OnInit } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { finalize } from 'rxjs';
import { BaseModalComponent } from '@card-software/library';
import { InventorySessionProxyService } from '../../../proxies/inventory/inventory-session.proxy.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { InventorySession } from '../../../models/inventory-session/inventory-session';
import { LocationNode } from '../../../models/location';
import _ from 'lodash';

@Component({
  selector: 'app-create-child-session-dialog',
  templateUrl: './create-child-session-dialog.component.html',
  styleUrls: ['./create-child-session-dialog.component.scss'],
})
export class CreateChildSessionDialogComponent extends BaseModalComponent<InventorySession | null> implements OnInit {
  @Input() parentId!: string;
  @Input() parentLocationCode!: string;

  locationTreeNodes: TreeNode[] = [];
  selectedNode: TreeNode | null = null;
  isLoadingLocations = false;
  isSaving = false;

  constructor(
    injector: Injector,
    private proxyService: InventorySessionProxyService,
    private locationProxyService: LocationProxyService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadLocationTree();
  }

  // The panel is appended to <body>, so PrimeNG sizes it to its content rather than to the field. Match
  // the field's width so a long location name wraps inside the panel instead of spilling off-screen.
  matchPanelWidth(event: { overlay?: HTMLElement; target?: HTMLElement }): void {
    const { overlay, target } = event;
    if (!overlay || !target) return;
    overlay.style.width = `${target.offsetWidth}px`;
  }

  private loadLocationTree(): void {
    this.isLoadingLocations = true;
    this.locationProxyService
      .getAllChildrenLocations(this.parentLocationCode)
      .pipe(finalize(() => (this.isLoadingLocations = false)))
      .subscribe({
        next: (result) => {
          const locations = result?.data || [];
          this.locationTreeNodes = this.buildTreeNodes(locations);
        },
      });
  }

  private buildTreeNodes(locations: LocationNode[]): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    const nodeMap = new Map<string, TreeNode>();

    // First pass: create all nodes — non-storable locations are selectable
    for (const loc of locations) {
      nodeMap.set(loc.locationCode, {
        key: loc.locationCode,
        label: loc.code + ' - ' + loc.name.en,
        data: loc,
        leaf: true,
        selectable: false,
      });
    }

    // Second pass: build hierarchy
    for (const loc of locations) {
      const node = nodeMap.get(loc.locationCode)!;

      if (loc.parentCode) {
        const parentNode = nodeMap.get(loc.parentCode);
        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
            parentNode.leaf = false;
          }
          parentNode.children.push(node);
        } else {
          treeNodes.push(node);
        }
      } else {
        treeNodes.push(node);
      }
    }

    //get those that do not have children and are storable
    for (const node of treeNodes) {
      this.markSelectableNodes(node);
    }

    return treeNodes;
  }

  private markSelectableNodes(node: TreeNode): void {
    if (_.isEmpty(node.children)) {
      node.selectable = true;
    } else {
      for (const child of node.children as TreeNode[]) {
        this.markSelectableNodes(child);
      }
    }
  }

  onSave(): void {
    if (!this.selectedNode || this.isSaving) return;

    const locationCode = this.selectedNode.key as string;
    this.isSaving = true;
    this.proxyService
      .createChild(this.parentId, { locationCode })
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (response) => {
          if (response?.data) {
            GlobalMessageUtil.success('Success', `Child session created for ${response.data.locationCode}`);
            this.close({ action: 'save', data: response.data });
          }
        },
      });
  }

  onClose(): void {
    this.close({ action: 'Cancel' });
  }
}
